/**
 * Deployment Detection and Auto-Refresh Utility
 * 
 * This utility helps detect when a new deployment has occurred and
 * automatically refreshes the page to load the new version.
 */

// Global notification handlers - will be set by the app
let globalAddNotification: ((notification: any) => void) | null = null;

export function setNotificationHandler(addNotification: (notification: any) => void) {
  globalAddNotification = addNotification;
}

interface DeploymentConfig {
  checkInterval: number; // How often to check (in milliseconds)
  retryDelay: number; // Delay before retrying failed requests
  maxRetries: number; // Maximum number of retries
  enableChunkErrorDetection: boolean; // Enable immediate chunk error detection
  enableVersionCheck: boolean; // Enable version-based detection
}

const defaultConfig: DeploymentConfig = {
  checkInterval: 60000, // Check every 60 seconds (reduced frequency)
  retryDelay: 3000, // Wait 3 seconds before retry
  maxRetries: 3,
  enableChunkErrorDetection: true,
  enableVersionCheck: true,
};

// Declare global variables that are injected by Vite
declare global {
  const __BUILD_VERSION__: string;
}

class DeploymentDetector {
  private config: DeploymentConfig;
  private checkIntervalId: number | null = null;
  private initialVersion: string | null = null;
  private hasShownReloadNotification = false;

  constructor(config: Partial<DeploymentConfig> = {}) {
    this.config = { ...defaultConfig, ...config };
    
    // Get initial build version
    this.initialVersion = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : null;
    
    // Debug logging
    console.log('DeploymentDetector initialized', {
      initialVersion: this.initialVersion,
      buildVersionDefined: typeof __BUILD_VERSION__ !== 'undefined',
      buildVersionValue: typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'undefined'
    });
  }

  /**
   * Start monitoring for deployments
   */
  start(): void {
    if (typeof window === 'undefined') return;

    // Set up chunk error detection
    if (this.config.enableChunkErrorDetection) {
      this.setupChunkErrorDetection();
    }

    // Start periodic version checks
    if (this.config.enableVersionCheck) {
      this.checkIntervalId = window.setInterval(() => {
        this.checkForDeployment();
      }, this.config.checkInterval);
    }

    console.log('Deployment detector started', {
      initialVersion: this.initialVersion,
      chunkErrorDetection: this.config.enableChunkErrorDetection,
      versionCheck: this.config.enableVersionCheck
    });
  }

  /**
   * Stop monitoring
   */
  stop(): void {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
    }
  }

  /**
   * Set up immediate chunk error detection
   */
  private setupChunkErrorDetection(): void {
    // Handle unhandled promise rejections (common for dynamic imports)
    window.addEventListener('unhandledrejection', (event) => {
      if (this.isChunkLoadError(event.reason)) {
        console.log('Chunk load error detected via unhandled rejection:', event.reason);
        event.preventDefault(); // Prevent default error handling
        this.handleChunkLoadError();
      }
    });

    // Handle regular errors
    window.addEventListener('error', (event) => {
      const isChunk = this.isChunkLoadError(event.error);
      const isResource = this.isResourceLoadError(event);
      if (isChunk || isResource) {
        console.log('Chunk/Resource load error detected via error event:', event.error || event.message);
        event.preventDefault();
        this.handleChunkLoadError();
      }
    });
  }

  /**
   * Check if an error is related to chunk loading
   */
  private isChunkLoadError(error: any): boolean {
    if (!error) return false;
    
    const message = error.message || error.toString();
    const stack = error.stack || '';
    
    // Common chunk loading error patterns
    const chunkErrorPatterns = [
      /Failed to fetch dynamically imported module/i,
      /Loading chunk \d+ failed/i,
      /ChunkLoadError/i,
      /Loading CSS chunk/i,
      /Loading chunk/i,
      /Failed to import/i,
      /Cannot read properties of undefined.*\.js/i,
      /Unexpected token.*in JSON/i, // Sometimes manifests as JSON parsing errors
      /NetworkError.*chunk/i,
      /TypeError.*chunk/i,
    ];

    return chunkErrorPatterns.some(pattern => pattern.test(message) || pattern.test(stack));
  }

  /**
   * Check if this is a resource loading error that might indicate deployment issues
   */
  private isResourceLoadError(event: ErrorEvent): boolean {
    const target = event.target as HTMLElement;
    const message = event.message || '';
    
    // Check for script loading failures
    if (target?.tagName === 'SCRIPT' && target.getAttribute('src')?.includes('assets')) {
      return true;
    }
    
    // Check for CSS loading failures
    if (target?.tagName === 'LINK' && target.getAttribute('href')?.includes('assets')) {
      return true;
    }

    // Check for specific error messages
    return /Failed to load resource.*\.js$/.test(message) || 
           /Failed to load resource.*\.css$/.test(message);
  }

  /**
   * Handle chunk loading errors immediately
   */
  private handleChunkLoadError(): void {
    if (this.hasShownReloadNotification) return;
    
    this.hasShownReloadNotification = true;
    this.stop(); // Stop periodic checks
    
    console.log('Chunk loading error detected - likely new deployment');
    
    // Clear service worker caches if available
    this.clearCaches();
    
    // Show user notification
    this.showReloadNotification('Detektovana je nova verzija aplikacije. Stranica će biti osvežena.');
  }

  /**
   * Clear browser caches
   */
  private async clearCaches(): Promise<void> {
    try {
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('Browser caches cleared');
      }
    } catch (error) {
      console.warn('Failed to clear caches:', error);
    }
  }

  /**
   * Show reload notification to user
   */
  private showReloadNotification(message: string): void {
    // Use the notification bell if available
    if (globalAddNotification) {
      globalAddNotification({
        title: 'Ažuriranje aplikacije',
        message: message,
        type: 'warning',
        action: {
          label: 'Osveži stranicu',
          onClick: () => {
            console.log('User clicked refresh from notification');
            window.location.reload();
          }
        }
      });
      
      // Auto reload after a delay
      setTimeout(() => {
        console.log('Auto-reloading page due to chunk loading error...');
        window.location.reload();
      }, 5000); // Give user 5 seconds to react
      
    } else {
      // Fallback to browser notification and confirm dialog
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Ažuriranje aplikacije', {
          body: message,
          icon: '/favicon.ico',
          requireInteraction: true
        });
      }
      
      // Show browser confirm dialog as fallback
      const userWantsReload = confirm(message + ' Kliknite OK za osvežavanje stranice.');
      
      // Reload regardless of user choice (they need the new version)
      setTimeout(() => {
        console.log('Reloading page due to chunk loading error...');
        window.location.reload();
      }, userWantsReload ? 100 : 2000);
    }
  }

  /**
   * Check if a new deployment has occurred by comparing versions
   */
  private async checkForDeployment(): Promise<void> {
    try {
      // Try version endpoint first
      const versionResponse = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (versionResponse.ok) {
        const versionData = await versionResponse.json();
        
        // Debug logging to understand the comparison
        console.log('Version check:', {
          currentVersion: this.initialVersion,
          serverVersion: versionData.version,
          hasInitialVersion: !!this.initialVersion,
          hasServerVersion: !!versionData.version,
          areEqual: versionData.version === this.initialVersion
        });
        
        // Only trigger deployment if:
        // 1. We have both versions
        // 2. They are different
        // 3. We haven't already shown a notification
        if (versionData.version && this.initialVersion && 
            versionData.version !== this.initialVersion && 
            !this.hasShownReloadNotification) {
          console.log('New deployment detected via version endpoint!', {
            old: this.initialVersion,
            new: versionData.version
          });
          this.handleNewDeployment();
          return;
        }
        
        // If versions match or we don't have both versions, continue with normal operation
        return;
      }

      // Only use fallback if version endpoint fails
      console.warn('Version endpoint failed, using fallback method');
      await this.checkIndexHtml();
    } catch (error) {
      console.warn('Error checking for deployment:', error);
      // Try the fallback method only if the main method fails
      await this.checkIndexHtml();
    }
  }

  /**
   * Fallback method: check index.html for script changes
   * This method is more conservative to avoid false positives
   */
  private async checkIndexHtml(): Promise<void> {
    // Skip HTML checking if we don't have a reliable version check
    if (!this.initialVersion) {
      console.log('Skipping HTML check - no initial version available');
      return;
    }
    
    try {
      // Fetch the index.html with cache busting
      const response = await fetch(`/index.html?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        console.warn('Failed to check index.html for deployment:', response.status);
        return;
      }

      const html = await response.text();
      
      // Extract script sources from the HTML
      const scriptMatches = html.match(/<script[^>]*src="([^"]*)"[^>]*>/g);
      
      if (scriptMatches) {
        let failedScripts = 0;
        const totalScripts = scriptMatches.length;
        
        for (const scriptMatch of scriptMatches) {
          const srcMatch = scriptMatch.match(/src="([^"]*)"/);
          if (srcMatch) {
            const src = srcMatch[1];
            
            // Only check main application scripts, not external ones
            if ((src.includes('index') || src.includes('main')) && src.includes('assets/js/')) {
              // Try to fetch this script to see if it exists
              try {
                const scriptResponse = await fetch(src, { method: 'HEAD', cache: 'no-cache' });
                if (!scriptResponse.ok) {
                  failedScripts++;
                  console.log('Script not found:', src);
                }
              } catch {
                failedScripts++;
                console.log('Failed to fetch script:', src);
              }
            }
          }
        }
        
        // Only trigger deployment detection if multiple scripts are missing
        // This reduces false positives from temporary network issues
        if (failedScripts > 0 && failedScripts >= Math.min(2, totalScripts * 0.5)) {
          console.log(`Multiple scripts missing (${failedScripts}/${totalScripts}), indicates new deployment`);
          this.handleNewDeployment();
        }
      }
    } catch (error) {
      console.warn('Error checking index.html for deployment:', error);
    }
  }

  /**
   * Handle when a new deployment is detected
   */
  private handleNewDeployment(): void {
    if (this.hasShownReloadNotification) return;
    
    this.hasShownReloadNotification = true;
    this.stop(); // Stop checking
    
    // Clear caches
    this.clearCaches();
    
    // Show reload notification
    this.showReloadNotification('Nova verzija aplikacije je dostupna.');
  }

  /**
   * Force a deployment check
   */
  checkNow(): void {
    this.checkForDeployment();
  }
}

// Create a singleton instance
export const deploymentDetector = new DeploymentDetector();

/**
 * Initialize deployment detection
 */
export function initDeploymentDetection(): void {
  deploymentDetector.start();
}

/**
 * Check for deployment immediately
 */
export function checkForDeploymentNow(): void {
  deploymentDetector.checkNow();
}

export default deploymentDetector; 